import { Injectable, inject } from '@angular/core';

import { Observable, map, switchMap } from 'rxjs';

import { ExtendedTab, GlobalMyaccountSubscriptionsRoutes } from '@hdms/shared/common-types';
import { CustomLinkExtendedTab, CustomLinksService, FeatureToggleService } from '@hdms/shared/util-core';

import { FT_CONSOPASS_REPORTING, FinHubModuleRoute } from '../constants';
import { FinHubTab } from '../types/tabs.enum';

import { AccessService } from './access.service';

export interface EnabledExtendedTab extends CustomLinkExtendedTab {
  enabled?: (accessService: AccessService) => boolean;
}

const FINHUB_NAV_TABS: ExtendedTab[] = [
  {
    id: FinHubTab.ORDERS,
    url: FinHubModuleRoute.ORDERS_MODULE,
    label: FinHubTab.ORDERS
  },
  {
    id: FinHubTab.QUOTES,
    url: FinHubModuleRoute.QUOTES_MODULE,
    label: FinHubTab.QUOTES
  }
];

enum FinHubHeaderNavItem {
  SHIPMENT = 'shipment',
  REORDER = 'reorder',
  INVOICE = 'invoice',
  PAY_CENTER = 'payCenter',
  CCP_PORTAL = 'ccpPortal',
  SOFTWARE_SUBSCRIPTIONS = 'softwareSubscriptions',
  QUOTE_FOR_CONTRACT = 'quoteForContract',
  CONSOPASS_REPORTING = 'consopassReporting'
}

const FINHUB_HEADER_LINKS: EnabledExtendedTab[] = [
  ...FINHUB_NAV_TABS,
  {
    id: FinHubHeaderNavItem.SHIPMENT,
    url: FinHubModuleRoute.ORDERS_MODULE,
    label: FinHubHeaderNavItem.SHIPMENT,
    enabled: a => !a.isByDesign
  },
  {
    id: FinHubHeaderNavItem.REORDER,
    url: FinHubModuleRoute.ORDERS_MODULE,
    label: FinHubHeaderNavItem.REORDER
  },
  {
    id: FinHubHeaderNavItem.INVOICE,
    url: FinHubModuleRoute.ORDERS_MODULE,
    label: FinHubHeaderNavItem.INVOICE,
    enabled: a => !a.isByDesign
  },
  {
    id: FinHubHeaderNavItem.PAY_CENTER,
    url: c => c.get('PAY_CENTER'),
    label: FinHubHeaderNavItem.PAY_CENTER,
    enabled: a => !a.isCcpLinkEnabled
  },
  {
    id: FinHubHeaderNavItem.CCP_PORTAL,
    url: c => c.get('CCP_PORTAL_CUSTOMER'),
    label: FinHubHeaderNavItem.CCP_PORTAL,
    enabled: a => a.isCcpLinkEnabled && !a.isAccountManager
  },
  {
    id: FinHubHeaderNavItem.CCP_PORTAL,
    url: c => c.get('CCP_PORTAL_AM'),
    label: FinHubHeaderNavItem.CCP_PORTAL,
    enabled: a => a.isCcpLinkEnabled && a.isAccountManager
  },
  {
    id: FinHubHeaderNavItem.SOFTWARE_SUBSCRIPTIONS,
    url: `/${GlobalMyaccountSubscriptionsRoutes.BASE}/software`,
    label: FinHubHeaderNavItem.SOFTWARE_SUBSCRIPTIONS,
    enabled: a => a.includeSoftwareSelling
  },
  {
    id: FinHubHeaderNavItem.QUOTE_FOR_CONTRACT,
    url: c => c.get('QUOTE_FOR_CONTRACT'),
    label: FinHubHeaderNavItem.QUOTE_FOR_CONTRACT,
    enabled: a => a.includeQuoteForContract
  },
  {
    id: FinHubHeaderNavItem.CONSOPASS_REPORTING,
    url: FinHubModuleRoute.CONSUMABLE_REPORTING_MODULE,
    label: FinHubHeaderNavItem.CONSOPASS_REPORTING,
    enabled: a => a.isConsopassReportingLinkEnabled
  }
];

/**
 * Purpose of this service is to centralize the primary link configuration for entering
 * FinHub feature, displayed in ribbon tab set as well as user account menu in header component.
 * Both have to rely on one single source of truth based on same permission conditions.
 */
@Injectable({
  providedIn: 'root'
})
export class FinHubNavigationService {
  private readonly accessService = inject(AccessService);
  private readonly customLinkService = inject(CustomLinksService);
  private readonly ftService = inject(FeatureToggleService);

  getTabNavigation(): ExtendedTab[] {
    return FINHUB_NAV_TABS;
  }

  getNavigation$(): Observable<ExtendedTab[]> {
    return this.ftService.getToggleById(FT_CONSOPASS_REPORTING)
      ? this.accessService
          .isFinHubAccessPermitted$()
          .pipe(
            switchMap(permitted =>
              permitted
                ? this.accessService.getConsumableSubscriptionStatus$().pipe(map(() => this.getAvailableHeaderLinks()))
                : []
            )
          )
      : this.accessService
          .isFinHubAccessPermitted$()
          .pipe(map(permitted => (permitted ? this.getAvailableHeaderLinks() : [])));
  }

  private getAvailableHeaderLinks(): ExtendedTab[] {
    return FINHUB_HEADER_LINKS.map(link => ({
      ...link,
      url: this.customLinkService.parseUrl(link.url)
    })).filter(link => !link.enabled || link.enabled(this.accessService));
  }
}
